/**
 * 컬렉션(보유 카드) API — 기획확정안 §6.
 *
 * BE: CollectionController @ /api/v1/user/collection (전부 인증 필요 — §6.1 "비회원은 저장 불가")
 *   GET    /                          보유 목록 + 요약(§6.3) + 세트별 진행률
 *   POST   /holdings                  등록
 *   PUT    /holdings/{id}             수정
 *   DELETE /holdings/{id}             삭제
 *   POST   /photos                    실물사진 업로드 (multipart)
 *
 * ⚠ 스캔 기록과 컬렉션은 다른 개념이다(§6). 스캔했다고 여기 자동으로 들어오지 않는다 —
 *   사용자가 결과에서 고른 카드만 담긴다.
 */
import { apiGet, apiPost, apiPut, apiDelete } from "./client";
import { uploadMultipart } from "./upload";
import type { PriceView } from "./card";

/** 보유 행에 붙는 카드 정보 — 대표가 대신 보유 배리언트의 시세(currentPrice)를 본다. */
export interface HoldingCard {
  cardSerialNo: number;
  cardUid: string;
  name: string;
  /** 번호 없는 인쇄판(일본판 기본 에너지 등)이 실재해 null 이 온다 */
  number: string | null;
  language: string;
  rarity: string | null;
  imagePathway: string | null;
  cardsetSerialNo: number;
  cardsetName: string;
  cardsetCode: string;
}

/** 보유 한 건 = 취득 한 건. 같은 카드라도 산 시점이 다르면 행이 다르다. */
export interface HoldingItem {
  holdingSerialNo: number;
  card: HoldingCard;
  quantity: number;
  /** null = 구매가 미입력. 손익 집계에서 빠진다 */
  purchasePrice: number | null;
  purchaseDate: string | null;
  purchaseSource: string | null;
  currency: string;
  condition: string | null;
  finish: string;
  pattern: string;
  grader: string;
  grade: string;
  gradeCertNo: string | null;
  keepPlace: string | null;
  photoAttachFileSerialNo: number | null;
  photoUrl: string | null;
  memo: string | null;
  /** 이 보유 조합(피니시·패턴·감정등급)의 현재 시세 */
  currentPrice: PriceView;
  registeredAt: string;
}

/**
 * §6.3 요약.
 *
 * ⚠ 평가액과 취득원가는 **분모가 다르다**. 구매가를 안 적은 행, 시세가 아직 없는 카드가
 * 정상적으로 존재하기 때문이다. `valuedQuantity`/`costedQuantity` 로 "일부만 집계됨"을
 * 표시할 수 있고, 두 합계를 그냥 빼면 손익이 거짓말이 된다.
 */
export interface CollectionSummary {
  holdingCount: number;
  totalQuantity: number;
  totalValue: number;
  totalCost: number;
  valuedQuantity: number;
  costedQuantity: number;
  /**
   * 손익 계산이 가능한 보유분(구매가 O **그리고** 시세 O)의 평가액/취득원가 쌍.
   *
   * ⚠ 손익은 반드시 `pnlValue - pnlCost` 로 낸다. `totalValue - totalCost` 는 **틀린 값**이다
   * — 두 합계의 모수가 다르다(구매가만 아는 카드, 시세만 아는 카드가 각각 정상 존재).
   */
  pnlValue: number;
  pnlCost: number;
  /** 손익에 들어간 장수 — "N장 기준" 안내용 */
  pnlQuantity: number;
}

/** §6.3 세트별 분류 — 보유 **종** 수 / 세트 총 카드 수(수량 아님). */
export interface SetProgress {
  cardsetSerialNo: number;
  cardsetName: string;
  cardsetCode: string;
  seriesName: string | null;
  ownedCardCount: number;
  totalCardCount: number;
}

export interface CollectionResponse {
  holdings: HoldingItem[];
  summary: CollectionSummary;
  setProgress: SetProgress[];
}

/** 등록·수정 요청 — 필수는 카드·수량뿐(§6.2 는 "저장 **가능한**" 정보). */
export interface HoldingSaveRequest {
  cardSerialNo: number;
  quantity: number;
  purchasePrice?: number | null;
  purchaseDate?: string | null;
  purchaseSource?: string | null;
  currency?: string;
  condition?: string | null;
  finish?: string | null;
  pattern?: string | null;
  grader?: string | null;
  grade?: string | null;
  gradeCertNo?: string | null;
  keepPlace?: string | null;
  photoAttachFileSerialNo?: number | null;
  memo?: string | null;
}

export interface PhotoUploadResponse {
  attachFileSerialNo: number;
  originalFileName: string;
  photoUrl: string;
}

export async function fetchCollection(): Promise<CollectionResponse> {
  const res = await apiGet<CollectionResponse | null>("/collection");
  // 응답이 비어도 화면은 빈 컬렉션으로 그린다 — null 체크를 페이지마다 되풀이하지 않는다.
  return (
    res ?? {
      holdings: [],
      summary: {
        holdingCount: 0,
        totalQuantity: 0,
        totalValue: 0,
        totalCost: 0,
        valuedQuantity: 0,
        costedQuantity: 0,
        pnlValue: 0,
        pnlCost: 0,
        pnlQuantity: 0,
      },
      setProgress: [],
    }
  );
}

export function createHolding(req: HoldingSaveRequest): Promise<HoldingItem> {
  return apiPost<HoldingItem>("/collection/holdings", req);
}

export function updateHolding(holdingSerialNo: number, req: HoldingSaveRequest): Promise<HoldingItem> {
  return apiPut<HoldingItem>(`/collection/holdings/${holdingSerialNo}`, req);
}

export function deleteHolding(holdingSerialNo: number): Promise<void> {
  return apiDelete(`/collection/holdings/${holdingSerialNo}`);
}

/** 사진 먼저 올리고 받은 id 를 저장 요청에 싣는다 (문의 첨부와 같은 2단 흐름). */
export function uploadHoldingPhoto(file: File): Promise<PhotoUploadResponse> {
  return uploadMultipart<PhotoUploadResponse>("/collection/photos", file);
}
